// react
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useParams, useRouter } from '@tanstack/react-router';

// api
import { useUpdatePortfolioAnalysisNowUpdatePortfolioAnalysisNowAccountIdPutMutation } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';
import { useSetAddressInfoForMeterMeterAddressAnalysisTypeAccountIdMeterIdPutMutation } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMeters';
import {
  type PmMeterType,
  useAddPmmMeterPmmMeterAccountIdUnitIdPostMutation,
} from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisPMM';

// shared components
import PageContainer from '@/components/layout/PageContainer';

// feature-components
import CreateSiteForm from '@/features/energyxplorer/portfolio/forms/CreateSiteForm';
import type { CreateSiteFormValues } from '@/features/energyxplorer/portfolio/forms/createSiteForm.schema';
// feature-utils
import { getMeterId } from '@/features/energyxplorer/portfolio/utils/meterUtils';
// redux
import { showSnackbar } from '@/redux/snackbarSlice';

export const Route = createFileRoute(
  '/$accountId/_user/energyxplorer/portfolio/site/create',
)({
  component: CreateSitePage,
});

function CreateSitePage() {
  const router = useRouter();
  const { accountId } = useParams({ strict: false }) as { accountId: string };
  const dispatch = useDispatch();
  const { t } = useTranslation('energyxplorer');

  const [putMeterAddress, { isLoading: isPuttingAddress }] =
    useSetAddressInfoForMeterMeterAddressAnalysisTypeAccountIdMeterIdPutMutation();
  const [addPmmMeter, { isLoading: isAddingPmm }] =
    useAddPmmMeterPmmMeterAccountIdUnitIdPostMutation();
  const [updatePortfolioAnalysis] =
    useUpdatePortfolioAnalysisNowUpdatePortfolioAnalysisNowAccountIdPutMutation();

  const handleSubmit = async (values: CreateSiteFormValues) => {
    const addressArgs =
      values.siteMode === 'existing' && values.existingAddress
        ? {
            postalCode: values.existingAddress.ZipCode || '',
            street: values.existingAddress.StreetName || '',
            buildingNumber: values.existingAddress.StreetNumber || '',
            city: values.existingAddress.City,
            matrikelKey: values.existingAddress.matrikelKey,
          }
        : {
            postalCode: values.newAddress?.zipCode || '',
            street: values.newAddress?.street || '',
            buildingNumber: values.newAddress?.houseNumber || '',
            city: values.newAddress?.city,
            matrikelKey: values.newAddress?.cadastreKey,
          };

    if (values.meter?.source === 'portfolio') {
      const result = await putMeterAddress({
        analysisType: values.meter.analysisType,
        accountId: accountId as unknown as number,
        meterId: getMeterId(values.meter.meter),
        ...addressArgs,
      });

      if (!('error' in result)) {
        await updatePortfolioAnalysis({
          accountId: accountId as unknown as number,
        });
        dispatch(
          showSnackbar({
            message: t('site.create_success') || 'Site created successfully',
            severity: 'success',
          }),
        );
        router.history.back();
      }
    } else if (values.meter?.source === 'pmm') {
      const result = await addPmmMeter({
        accountId: accountId as unknown as number,
        unitId: values.meter.unitId,
        meterType: values.meterType as PmMeterType,
        ...addressArgs,
      });

      if (!('error' in result)) {
        dispatch(
          showSnackbar({
            message: t('site.create_success') || 'Site created successfully',
            severity: 'success',
          }),
        );
        router.history.back();
      }
    }
  };

  const handleCancel = () => {
    router.history.back();
  };

  return (
    <PageContainer>
      <CreateSiteForm
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isPuttingAddress || isAddingPmm}
      />
    </PageContainer>
  );
}
